import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CANALES, CANAL_LABEL, type CanalMesPoint } from '../../lib/api.js';
import { fmtInt, fmtPct } from '../../lib/format.js';
import { AXIS, CANAL_COLORS, GRID, SURFACE, TOOLTIP_STYLE } from './ChartCard.js';

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/** Prefijo de las claves con el valor absoluto, que solo se leen en el tooltip. */
const NOCHES = 'noches:';

/**
 * Peso de cada canal DENTRO de cada mes, en barras apiladas al 100 %.
 *
 * La pregunta es «cómo se reparte el mes entre canales», y en noches absolutas
 * un mes flojo se ve más bajo aunque su reparto sea idéntico: al normalizar,
 * los meses se comparan entre sí. El volumen no se pierde — las noches de cada
 * tramo y el total del mes viven en el tooltip.
 *
 * Solo entran los meses CON DATO: un mes sin cargar dibujado en cero se leería
 * como «ningún canal vendió», que es justo lo contrario de «no sabemos».
 */
export function CanalesMensualChart({ meses }: { meses: CanalMesPoint[] }) {
  const data = meses
    .filter((m) => m.conDato)
    .map((m) => {
      const fila: Record<string, string | number> = {
        label: MES_CORTO[m.mes - 1]!,
        total: m.total,
      };
      for (const canal of CANALES) {
        const noches = m.porCanal[canal] ?? 0;
        fila[CANAL_LABEL[canal]] = m.total > 0 ? (noches / m.total) * 100 : 0;
        fila[`${NOCHES}${CANAL_LABEL[canal]}`] = noches;
      }
      return fila;
    });

  if (data.length === 0) {
    return <p className="text-sm text-slate-500">Sin meses cargados en el período.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="label" tick={AXIS} />
        <YAxis tick={AXIS} unit="%" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={(label: string, payload) => {
            const total = payload?.[0]?.payload?.total as number | undefined;
            return total == null ? label : `${label} · ${fmtInt(total)} noches`;
          }}
          formatter={(v: number, name: string, item: { payload?: Record<string, number> }) => [
            `${fmtPct(v)} · ${fmtInt(item?.payload?.[`${NOCHES}${name}`])} noches`,
            name,
          ]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {CANALES.map((canal, i) => (
          <Bar
            key={canal}
            dataKey={CANAL_LABEL[canal]}
            name={CANAL_LABEL[canal]}
            stackId="canales"
            fill={CANAL_COLORS[canal]}
            // 2 px del color de la superficie entre segmentos: los separa sin
            // meter una línea de contorno que compita con el dato.
            stroke={SURFACE}
            strokeWidth={2}
            radius={i === CANALES.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
